/**
 * Custom Features - Merged JavaScript
 * 
 * This file combines all custom feature functionality:
 * 1. Image Protection
 * 2. Coupon Banner
 * 3. Delivery Time Block
 * 4. Product Detail Pop
 * 
 * P1-2: Product detail pop — single placeholder + one MutationObserver.
 * P1-3: Delivery tooltip — document delegation for outside-click / Escape (one listener each).
 */

(function() {
  'use strict';

  // ============================================
  // UNIFIED INITIALIZATION SYSTEM
  // ============================================
  
  // 统一的 DOM 就绪处理队列
  const initQueue = [];
  let domReady = false;

  function addInit(fn) {
    if (domReady) {
      fn();
    } else {
      initQueue.push(fn);
    }
  }

  function runInitQueue() {
    domReady = true;
    initQueue.forEach(function(fn) {
      try {
        fn();
      } catch (e) {
        console.error('Init function error:', e);
      }
    });
    initQueue.length = 0;
  }

  // DOM 就绪检测
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitQueue);
  } else {
    runInitQueue();
  }

  // ============================================
  // 1. IMAGE PROTECTION
  // ============================================
  
  function generateToken() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
  }
  
  function addWatermark(canvas, ctx) {
    var watermarkText = 'copyright © www.ceayabikes.com';
    ctx.textBaseline = 'middle';
    
    function measureText(fontSize) {
      ctx.font = 'bold ' + fontSize + 'px Arial';
      return ctx.measureText(watermarkText).width;
    }
    
    function calculateOptimalFontSize(maxWidth, maxHeight) {
      var fontSize = Math.min(maxWidth / watermarkText.length * 1.5, maxHeight / 2, 50);
      var measured = measureText(fontSize);
      while (measured > maxWidth * 0.9 && fontSize > 10) {
        fontSize -= 2;
        measured = measureText(fontSize);
      }
      return Math.max(fontSize, 12);
    }
    
    var patternSize = Math.min(canvas.width / 2.5, canvas.height / 2.5, 180);
    var patternFontSize = calculateOptimalFontSize(patternSize * 0.85, patternSize * 0.35);
    var angle = -Math.PI / 6;
    var offsets = [0, patternSize / 2];
    
    ctx.save();
    ctx.font = 'bold ' + patternFontSize + 'px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.lineWidth = Math.max(1.5, patternFontSize / 15);
    ctx.textAlign = 'center';
    
    for (var i = 0; i < offsets.length; i++) {
      for (var j = 0; j < offsets.length; j++) {
        for (var x = offsets[i]; x < canvas.width + patternSize; x += patternSize) {
          for (var y = offsets[j]; y < canvas.height + patternSize; y += patternSize) {
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(angle);
            ctx.strokeText(watermarkText, 0, 0);
            ctx.fillText(watermarkText, 0, 0);
            ctx.restore();
          }
        }
      }
    }
    ctx.restore();
    
    var centerFontSize = calculateOptimalFontSize(canvas.width * 0.7, canvas.height * 0.25);
    ctx.save();
    ctx.font = 'bold ' + centerFontSize + 'px Arial';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.65)';
    ctx.lineWidth = Math.max(2.5, centerFontSize / 12);
    ctx.textAlign = 'center';
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(angle);
    ctx.strokeText(watermarkText, 0, 0);
    ctx.fillText(watermarkText, 0, 0);
    ctx.restore();
  }
  
  function createWatermarkedImage(img, callback) {
    if (!img) {
      callback(null);
      return;
    }
    
    function processImage(sourceImg) {
      try {
        var canvas = document.createElement('canvas');
        var ctx = canvas.getContext('2d');
        var width = sourceImg.naturalWidth || sourceImg.width;
        var height = sourceImg.naturalHeight || sourceImg.height;
        
        if (width <= 0 || height <= 0) {
          callback(null);
          return;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(sourceImg, 0, 0, width, height);
        addWatermark(canvas, ctx);
        canvas.toBlob(function(blob) {
          callback(blob || null);
        }, 'image/png', 0.95);
      } catch (e) {
        callback(null);
      }
    }
    
    if (img.complete && img.naturalWidth > 0) {
      processImage(img);
    } else {
      var imgSrc = img.src || img.currentSrc || img.dataset.src;
      if (!imgSrc) {
        callback(null);
        return;
      }
      var watermarkImg = new Image();
      watermarkImg.crossOrigin = 'anonymous';
      watermarkImg.onload = function() {
        processImage(watermarkImg);
      };
      watermarkImg.onerror = function() {
        if (img.complete && img.naturalWidth > 0) {
          processImage(img);
        } else {
          callback(null);
        }
      };
      watermarkImg.src = imgSrc;
    }
  }
  
  function downloadWatermarkedImage(img) {
    createWatermarkedImage(img, function(blob) {
      if (!blob) return;
      
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'image-' + generateToken() + '.png';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(function() {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
    });
  }
  
  function preventAllDrag(e) {
    var target = e.target;
    var isImg = target.tagName === 'IMG';
    var hasImg = target.closest && target.closest('img');
    var containsImg = target.querySelector && target.querySelector('img');
    
    if (isImg || hasImg || containsImg || target.tagName === 'PICTURE' || 
        (target.tagName === 'A' && (target.href && (target.href.match(/\.(webp|jpg|jpeg|png|gif)/i) || target.querySelector('img'))))) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'none';
        e.dataTransfer.dropEffect = 'none';
      }
      return false;
    }
  }
  
  function disableImageDrag(img) {
    if (!img || img.tagName !== 'IMG') return;
    img.setAttribute('draggable', 'false');
    img.style.webkitUserDrag = 'none';
    img.ondragstart = function() { return false; };
    var link = img.closest && img.closest('a');
    if (link) {
      link.setAttribute('draggable', 'false');
      link.ondragstart = function() { return false; };
    }
  }
  
  function initAllImages() {
    // 使用 requestIdleCallback 优化性能，避免阻塞图片加载
    if (window.requestIdleCallback) {
      requestIdleCallback(function() {
        document.querySelectorAll('img').forEach(disableImageDrag);
        document.querySelectorAll('a').forEach(function(link) {
          if (link.querySelector('img') || (link.href && link.href.match(/\.(webp|jpg|jpeg|png|gif)/i))) {
            link.setAttribute('draggable', 'false');
            link.ondragstart = function() { return false; };
          }
        });
      }, { timeout: 1000 });
    } else {
      // 降级方案：延迟执行，避免阻塞
      setTimeout(function() {
        document.querySelectorAll('img').forEach(disableImageDrag);
        document.querySelectorAll('a').forEach(function(link) {
          if (link.querySelector('img') || (link.href && link.href.match(/\.(webp|jpg|jpeg|png|gif)/i))) {
            link.setAttribute('draggable', 'false');
            link.ondragstart = function() { return false; };
          }
        });
      }, 100);
    }
  }

  // 初始化图片保护
  addInit(function() {
    initAllImages();
    
    document.addEventListener('dragstart', preventAllDrag, true);
    document.addEventListener('drag', preventAllDrag, true);
    document.addEventListener('dragend', preventAllDrag, true);
    
    document.addEventListener('mousedown', function(e) {
      var img = e.target.tagName === 'IMG' ? e.target : (e.target.closest && e.target.closest('img'));
      if (img) disableImageDrag(img);
    }, true);
    
    var touchData = { time: 0, img: null, x: 0, y: 0, moved: false, link: null, preventClick: false };
    
    document.addEventListener('touchstart', function(e) {
      var img = e.target.tagName === 'IMG' ? e.target : (e.target.closest && e.target.closest('img'));
      var link = img && img.closest ? img.closest('a') : null;
      if (img && (img.src || img.currentSrc || img.dataset.src)) {
        touchData.time = Date.now();
        touchData.img = img;
        touchData.link = link;
        touchData.moved = false;
        touchData.preventClick = false;
        if (e.touches && e.touches.length > 0) {
          touchData.x = e.touches[0].clientX;
          touchData.y = e.touches[0].clientY;
        }
      } else {
        touchData.img = null;
        touchData.link = null;
      }
    }, { passive: true });
    
    document.addEventListener('touchmove', function(e) {
      if (touchData.img && e.touches && e.touches.length > 0) {
        if (Math.abs(e.touches[0].clientX - touchData.x) > 10 || 
            Math.abs(e.touches[0].clientY - touchData.y) > 10) {
          touchData.moved = true;
        }
      }
    }, { passive: true });
    
    document.addEventListener('touchend', function(e) {
      if (touchData.img && !touchData.moved) {
        var touchDuration = Date.now() - touchData.time;
        var link = touchData.link;
        if (touchDuration > 800) {
          e.preventDefault();
          e.stopPropagation();
          touchData.preventClick = true;
          downloadWatermarkedImage(touchData.img);
          if (link) {
            link.style.pointerEvents = 'none';
            setTimeout(function() {
              link.style.pointerEvents = '';
            }, 100);
          }
        }
      }
      touchData.img = null;
      touchData.link = null;
      touchData.moved = false;
    }, { passive: false });
    
    document.addEventListener('click', function(e) {
      if (touchData.preventClick) {
        e.preventDefault();
        e.stopPropagation();
        touchData.preventClick = false;
      }
    }, true);
    
    document.addEventListener('contextmenu', function(e) {
      var img = e.target.tagName === 'IMG' ? e.target : (e.target.closest && e.target.closest('img'));
      var link = img && img.closest ? img.closest('a') : null;
      if (img || link) {
        e.preventDefault();
        if (img && (img.src || img.currentSrc || img.dataset.src)) {
          downloadWatermarkedImage(img);
        }
      }
    }, true);
  });

  // ============================================
  // 2. COUPON BANNER
  // ============================================
  
  // Countdown timer functionality
  class CouponTimer {
    constructor(element) {
      this.element = element;
      this.couponBanner = element.closest('.coupon-banner');
      this.endDate = new Date(element.dataset.endDate);
      this.daysEl = element.querySelector('.js-timer-days');
      this.hoursEl = element.querySelector('.js-timer-hours');
      this.minsEl = element.querySelector('.js-timer-mins');
      this.secsEl = element.querySelector('.js-timer-secs');
      
      if (this.isValid()) {
        this.init();
      } else {
        this.onExpire();
      }
    }

    isValid() {
      return !isNaN(this.endDate.getTime()) && this.endDate > new Date();
    }

    init() {
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    update() {
      const now = new Date();
      const diff = this.endDate - now;

      if (diff <= 0) {
        this.onExpire();
        return;
      }

      const totalSec = Math.floor(diff / 1000);
      const days = Math.floor(totalSec / 86400);
      const hours = Math.floor((totalSec % 86400) / 3600);
      const minutes = Math.floor((totalSec % 3600) / 60);
      const seconds = totalSec % 60;

      const pad2 = function (n) {
        return String(n).padStart(2, '0');
      };
      if (this.daysEl) this.daysEl.textContent = pad2(days);
      if (this.hoursEl) this.hoursEl.textContent = pad2(hours);
      if (this.minsEl) this.minsEl.textContent = pad2(minutes);
      if (this.secsEl) this.secsEl.textContent = pad2(seconds);
    }

    onExpire() {
      if (this.interval) {
        clearInterval(this.interval);
      }
      if (this.couponBanner) {
        this.couponBanner.classList.add('is-expired');
      }
    }
  }

  // 初始化优惠券横幅功能
  addInit(function() {
    // Toggle expand/collapse functionality
    const toggleButtons = document.querySelectorAll('.js-toggle-button');
    toggleButtons.forEach(button => {
      button.addEventListener('click', function() {
        const couponBanner = this.closest('.coupon-banner');
        const expandable = couponBanner?.querySelector('.js-expandable');
        const allToggles = couponBanner?.querySelectorAll('.js-toggle-button');
        
        if (!expandable) return;
        
        const isExpanded = expandable.classList.contains('is-expanded');
        
        if (isExpanded) {
          expandable.classList.remove('is-expanded');
          allToggles?.forEach(toggle => toggle.classList.remove('is-expanded'));
        } else {
          expandable.classList.add('is-expanded');
          allToggles?.forEach(toggle => toggle.classList.add('is-expanded'));
        }
      });
    });

    // Initialize countdown timers
    const timerElements = document.querySelectorAll('.js-timer');
    timerElements.forEach(element => {
      new CouponTimer(element);
    });

    // Handle copy icon button - reference native discount-code.js implementation
    const copyButtons = document.querySelectorAll('.coupon-banner__copy-icon.js-copy-button');
    copyButtons.forEach(button => {
      const couponBanner = button.closest('.coupon-banner');
      const discountCodeElement = couponBanner?.querySelector('.discount-code .js-discount-code');
      const successMessage = couponBanner?.querySelector('.discount-code .js-copy-success');
      
      // Check if clipboard API is available (same as native discount-code.js)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        button.addEventListener('click', function() {
          let discountCode = discountCodeElement?.textContent.trim();
          if (!discountCode && button.dataset.couponCode) {
            discountCode = String(button.dataset.couponCode).trim();
          }
          if (!discountCode) return;
          
          navigator.clipboard.writeText(discountCode).then(
            () => {
              // Show success message (same as native implementation)
              if (successMessage) {
                successMessage.classList.add('is-visible');
                successMessage.setAttribute('aria-hidden', 'false');
                
                setTimeout(() => {
                  successMessage.classList.add('is-closing');
                }, 1800);
                
                setTimeout(() => {
                  successMessage.classList.remove('is-closing');
                  successMessage.classList.remove('is-visible');
                }, 2000);
              }
            },
            () => {
              // Error handling (same as native: alert(theme.strings.discountCopyFail))
              const theme = window.theme || {};
              const alertMsg = theme?.strings?.discountCopyFail || 'Failed to copy discount code';
              alert(alertMsg); // eslint-disable-line
            }
          );
        });
      } else {
        // Hide button if clipboard API is not available (same as native: this.discountCodeBtn.hidden = true)
        button.hidden = true;
      }
    });
  });

  // ============================================
  // 3. DELIVERY TIME BLOCK
  // ============================================

  function addBusinessDays(startDate, days) {
    const currentDate = new Date(startDate);
    let addedDays = 0;
    const holidays = ['01-01', '12-24', '12-25'];

    function isHoliday(date) {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return holidays.includes(month + '-' + day);
    }

    function isWeekend(date) {
      const day = date.getDay();
      return day === 0 || day === 6;
    }

    while (addedDays < days) {
      currentDate.setDate(currentDate.getDate() + 1);
      if (!isWeekend(currentDate) && !isHoliday(currentDate)) {
        addedDays++;
      }
    }

    return currentDate;
  }

  function formatDeliveryDate(date) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    return month + ' ' + (day < 10 ? '0' + day : day);
  }

  function formatDeliveryDateRange(startDate, endDate) {
    if (startDate.getTime() === endDate.getTime()) {
      return formatDeliveryDate(startDate);
    }
    return formatDeliveryDate(startDate) + ' - ' + formatDeliveryDate(endDate);
  }

  function initDeliveryTimeDates() {
    const blocks = document.querySelectorAll('.delivery-time-block:not([data-delivery-dates-initialized="true"])');
    if (!blocks.length) return;

    blocks.forEach(function(block) {
      const dispatchTime = parseInt(block.dataset.dispatchTime || '1', 10);
      const minShippingTime = parseInt(block.dataset.minShippingTime || '0', 10);
      const maxShippingTime = parseInt(block.dataset.maxShippingTime || '0', 10);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const orderedDate = new Date(today);
      const orderedElement = block.querySelector('[data-delivery-date="ordered"]');
      if (orderedElement) {
        orderedElement.textContent = formatDeliveryDate(orderedDate);
      }

      const safeDispatchTime = Number.isNaN(dispatchTime) || dispatchTime < 1 ? 1 : dispatchTime;
      const dispatchStartDate = addBusinessDays(today, 1);
      const dispatchEndDate = addBusinessDays(today, safeDispatchTime);
      const dispatchElement = block.querySelector('[data-delivery-date="dispatch"]');
      if (dispatchElement) {
        if (safeDispatchTime === 1) {
          dispatchElement.textContent = formatDeliveryDate(dispatchStartDate);
        } else {
          dispatchElement.textContent = formatDeliveryDateRange(dispatchStartDate, dispatchEndDate);
        }
      }

      let deliveredStart = null;
      let deliveredEnd = null;
      if (minShippingTime > 0 || maxShippingTime > 0) {
        const deliveredElement = block.querySelector('[data-delivery-date="delivered"]');
        const shippingStartDate = addBusinessDays(dispatchEndDate, 1);

        if (minShippingTime > 0 && maxShippingTime > 0) {
          deliveredStart = addBusinessDays(shippingStartDate, minShippingTime);
          deliveredEnd = addBusinessDays(shippingStartDate, maxShippingTime);
          if (deliveredElement) {
            deliveredElement.textContent = formatDeliveryDateRange(deliveredStart, deliveredEnd);
          }
        } else if (minShippingTime > 0) {
          deliveredStart = addBusinessDays(shippingStartDate, minShippingTime);
          if (deliveredElement) {
            deliveredElement.textContent = formatDeliveryDate(deliveredStart) + '+';
          }
        } else if (maxShippingTime > 0) {
          deliveredEnd = addBusinessDays(shippingStartDate, maxShippingTime);
          if (deliveredElement) {
            deliveredElement.textContent = formatDeliveryDate(deliveredEnd);
          }
        }

        const estimatedDateRangeElement = block.querySelector('.delivery-time__estimated-date-range');
        if (estimatedDateRangeElement) {
          if (deliveredStart && deliveredEnd) {
            estimatedDateRangeElement.textContent = formatDeliveryDateRange(deliveredStart, deliveredEnd);
          } else if (deliveredStart) {
            estimatedDateRangeElement.textContent = formatDeliveryDate(deliveredStart) + '+';
          } else if (deliveredEnd) {
            estimatedDateRangeElement.textContent = formatDeliveryDate(deliveredEnd);
          }
        }
      }

      block.dataset.deliveryDatesInitialized = 'true';
    });
  }

  const deliveryTooltipByTrigger = new WeakMap();
  let deliveryTooltipDocumentDelegationBound = false;

  function bindDeliveryTooltipDocumentDelegation() {
    if (deliveryTooltipDocumentDelegationBound) return;
    deliveryTooltipDocumentDelegationBound = true;

    document.addEventListener(
      'click',
      function deliveryTooltipOutsideClick(e) {
        if (window.innerWidth > 768) return;

        const activeTriggers = document.querySelectorAll('.delivery-time__tooltip-trigger.active');
        if (!activeTriggers.length) return;

        for (let i = 0; i < activeTriggers.length; i++) {
          const trigger = activeTriggers[i];
          const state = deliveryTooltipByTrigger.get(trigger);
          if (!state) continue;

          const overlay = document.querySelector('.delivery-time-tooltip-overlay');
          if (overlay && e.target === overlay) {
            state.closeTooltip();
            return;
          }
          if (!trigger.contains(e.target) && !state.tooltip.contains(e.target)) {
            state.closeTooltip();
            return;
          }
        }
      },
      true
    );

    document.addEventListener('keydown', function deliveryTooltipEscape(e) {
      if (e.key !== 'Escape') return;

      document.querySelectorAll('.delivery-time__tooltip-trigger.active').forEach(function(trigger) {
        const state = deliveryTooltipByTrigger.get(trigger);
        if (state) state.closeTooltip();
      });
    });
  }
  
  // 初始化 tooltip 功能
  let initDeliveryTimeTooltipRetryCount = 0;
  const initDeliveryTimeTooltipMaxRetries = 40;

  function initDeliveryTimeTooltip() {
    const triggers = document.querySelectorAll('.delivery-time__tooltip-trigger');
    
    if (triggers.length === 0) {
      initDeliveryTimeTooltipRetryCount++;
      if (initDeliveryTimeTooltipRetryCount < initDeliveryTimeTooltipMaxRetries) {
        setTimeout(initDeliveryTimeTooltip, 100);
      }
      return;
    }

    initDeliveryTimeTooltipRetryCount = 0;
    
    triggers.forEach(function(trigger) {
      // 如果已经初始化过，跳过
      if (trigger.dataset.tooltipInitialized === 'true') {
        return;
      }
      trigger.dataset.tooltipInitialized = 'true';
      
      // 查找 tooltip - 优先在 trigger 内部查找
      let tooltip = trigger.querySelector('.delivery-time__tooltip');
      
      // 如果找不到，在整个文档中查找（可能被移到了其他地方）
      if (!tooltip) {
        const block = trigger.closest('.delivery-time-block');
        if (block) {
          tooltip = block.querySelector('.delivery-time__tooltip');
        }
      }
      
      // 如果还是找不到，跳过这个 trigger
      if (!tooltip) {
        console.warn('Tooltip not found for trigger:', trigger);
        return;
      }
      
      // 初始化这个 tooltip
      initTooltip(trigger, tooltip);
    });
  }
  
  // 初始化单个 tooltip
  function initTooltip(trigger, tooltip) {
    // 在事件处理时实时检测，而不是在初始化时固定
    function getIsMobile() {
      const width = window.innerWidth;
      const isMobile = width <= 768;
      return isMobile;
    }
    
    // 桌面端移除关闭按钮（如果存在）
    const existingCloseBtn = tooltip.querySelector('.delivery-time__tooltip-close');
    if (existingCloseBtn) {
      existingCloseBtn.remove();
    }
    
    // 打开 tooltip（改为类状态控制，避免内联 !important 与层级冲突）
    function openTooltip() {
      const isMobile = getIsMobile();

      document.querySelectorAll('.delivery-time__tooltip-trigger.active').forEach(function(t) {
        if (t !== trigger) t.classList.remove('active');
      });
      document.querySelectorAll('.delivery-time__tooltip.active').forEach(function(t) {
        if (t !== tooltip) {
          t.classList.remove('active');
          t.style.display = 'none';
        }
      });

      if (tooltip.parentNode !== document.body) {
        document.body.appendChild(tooltip);
      }

      trigger.classList.add('active');
      tooltip.classList.add('active');
      tooltip.style.display = 'block';

      if (isMobile) {
        let closeBtn = tooltip.querySelector('.delivery-time__tooltip-close');
        if (!closeBtn) {
          closeBtn = document.createElement('button');
          closeBtn.className = 'delivery-time__tooltip-close';
          closeBtn.type = 'button';
          closeBtn.setAttribute('aria-label', 'Close');
          closeBtn.textContent = 'Close';
          const content = tooltip.querySelector('.delivery-time__tooltip-content');
          if (content) content.appendChild(closeBtn);
        }
        if (closeBtn && !closeBtn.dataset.deliveryCloseBound) {
          closeBtn.dataset.deliveryCloseBound = 'true';
          closeBtn.addEventListener('click', closeBtnClickHandler);
        }

        let overlay = document.querySelector('.delivery-time-tooltip-overlay');
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'delivery-time-tooltip-overlay overlay--drawer';
          document.body.appendChild(overlay);
        }
        overlay.style.display = 'block';
        overlay.style.pointerEvents = 'auto';
        overlay.style.opacity = '1';

        tooltip.style.top = '';
        tooltip.style.left = '';
        tooltip.style.transform = '';
      } else {
        const closeBtn = tooltip.querySelector('.delivery-time__tooltip-close');
        if (closeBtn) closeBtn.remove();

        const triggerRect = trigger.getBoundingClientRect();
        tooltip.style.top = (triggerRect.bottom + 8) + 'px';
        tooltip.style.left = (triggerRect.left + triggerRect.width / 2) + 'px';
        tooltip.style.transform = 'translateX(-50%)';

        requestAnimationFrame(function() {
          const rect = tooltip.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          if (rect.right > viewportWidth) {
            tooltip.style.left = (triggerRect.right - 10) + 'px';
            tooltip.style.transform = 'translateX(-100%)';
          } else if (rect.left < 0) {
            tooltip.style.left = (triggerRect.left + 10) + 'px';
            tooltip.style.transform = 'none';
          }
          if (rect.bottom > viewportHeight) {
            tooltip.style.top = (triggerRect.top - rect.height - 8) + 'px';
          }
        });
      }
    }
    
    // 关闭 tooltip
    function closeTooltip() {
      const isMobile = getIsMobile();
      trigger.classList.remove('active');
      tooltip.classList.remove('active');

      if (isMobile) {
        const overlay = document.querySelector('.delivery-time-tooltip-overlay');
        if (overlay) {
          overlay.style.opacity = '0';
          overlay.style.pointerEvents = 'none';
          overlay.style.display = 'none';
        }
      }

      tooltip.style.display = 'none';
      tooltip.style.top = '';
      tooltip.style.left = '';
      tooltip.style.transform = '';
    }
    
    // 事件处理函数
    function clickHandler(e) {
      const isMobile = getIsMobile();
      if (isMobile) {
        e.preventDefault();
        e.stopPropagation();
        if (trigger.classList.contains('active')) {
          closeTooltip();
        } else {
          openTooltip();
        }
      } else {
        e.preventDefault();
        e.stopPropagation();
        if (trigger.classList.contains('active')) {
          closeTooltip();
        } else {
          openTooltip();
        }
      }
    }
    
    function mouseEnterHandler(e) {
      const isMobile = getIsMobile();
      if (!isMobile && !trigger.classList.contains('active')) {
        openTooltip();
      }
    }
    
    function mouseLeaveHandler(e) {
      const isMobile = getIsMobile();
      if (!isMobile) {
        setTimeout(() => {
          if (!trigger.matches(':hover') && !tooltip.matches(':hover')) {
            closeTooltip();
          }
        }, 100);
      }
    }
    
    function closeBtnClickHandler(e) {
      e.preventDefault();
      e.stopPropagation();
      closeTooltip();
    }
    
    // 事件监听
    trigger.addEventListener('click', clickHandler);
    trigger.addEventListener('mouseenter', mouseEnterHandler);
    trigger.addEventListener('mouseleave', mouseLeaveHandler);
    tooltip.addEventListener('mouseenter', mouseEnterHandler);
    tooltip.addEventListener('mouseleave', mouseLeaveHandler);

    deliveryTooltipByTrigger.set(trigger, { tooltip: tooltip, closeTooltip: closeTooltip });
    bindDeliveryTooltipDocumentDelegation();
    
    // 设置样式
    const initialIsMobile = getIsMobile();
    trigger.style.cursor = initialIsMobile ? 'pointer' : 'help';
    trigger.style.pointerEvents = 'auto';
  }
  
  // 初始化 delivery time tooltip
  addInit(function() {
    initDeliveryTimeDates();
    setTimeout(initDeliveryTimeTooltip, 50);
  });

  // ============================================
  // 4. PRODUCT DETAIL POP
  // ============================================
  
  // 全局变量：跟踪已加载的资源
  let chartJsLoaded = false;
  let chartJsLoading = false;

  function loadChartJS() {
    return new Promise(function(resolve, reject) {
      if (window.Chart) {
        chartJsLoaded = true;
        resolve();
        return;
      }

      if (chartJsLoading) {
        const checkInterval = setInterval(function() {
          if (window.Chart) {
            clearInterval(checkInterval);
            chartJsLoaded = true;
            resolve();
          }
        }, 50);
        return;
      }

      chartJsLoading = true;
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
      script.async = true;
      script.onload = function() {
        chartJsLoaded = true;
        chartJsLoading = false;
        resolve();
      };
      script.onerror = function() {
        chartJsLoading = false;
        reject(new Error('Failed to load Chart.js'));
      };
      document.head.appendChild(script);
    });
  }

  function initRadarChart(radarCanvas, radarData) {
    if (!radarData || !radarData.hasRadar) return;

    if (!radarCanvas) return;

    // 检查是否已经初始化过
    if (radarCanvas.chartInstance) {
      return;
    }

    const labels = radarData.labels || [];
    const dataValues = radarData.data || [];
    const minLength = Math.min(labels.length, dataValues.length);
    const chartLabels = labels.slice(0, minLength);
    
    // 验证并转换数据为数字
    const chartData = dataValues.slice(0, minLength).map(function(val) {
      const strVal = String(val).trim();
      if (strVal === '' || isNaN(strVal)) {
        console.warn('Invalid radar data value (non-numeric):', val);
        return 0;
      }
      const numVal = parseFloat(strVal);
      if (isNaN(numVal) || !isFinite(numVal)) {
        console.warn('Invalid radar data value (not a finite number):', val);
        return 0;
      }
      return numVal;
    });

    if (chartLabels.length === 0 || chartData.length === 0) return;

    const ctx = radarCanvas.getContext('2d');

    /** 与 custom-features.css 中 .product-detail-pop-radar-metric-dot 色板一致；首项随主题 --star-color */
    function getStarColorTriplet() {
      var raw = '';
      if (typeof getComputedStyle !== 'undefined') {
        raw = getComputedStyle(document.documentElement).getPropertyValue('--star-color').trim();
      }
      return raw || '225 107 17';
    }

    function tripletToRgb(triplet) {
      var parts = String(triplet).trim().split(/\s+/);
      if (parts.length < 3) return 'rgb(225, 107, 17)';
      return 'rgb(' + parts[0] + ', ' + parts[1] + ', ' + parts[2] + ')';
    }

    function tripletToRgba(triplet, alpha) {
      var parts = String(triplet).trim().split(/\s+/);
      if (parts.length < 3) return 'rgba(225, 107, 17, ' + alpha + ')';
      return 'rgba(' + parts[0] + ', ' + parts[1] + ', ' + parts[2] + ', ' + alpha + ')';
    }

    var starTriplet = getStarColorTriplet();
    var paletteRest = ['#6366f1', '#ea580c', '#64748b', '#c026d3', '#b8570d'];
    var pointBg = [];
    var pointBorder = [];
    var i;
    for (i = 0; i < chartData.length; i++) {
      if (i === 0) {
        pointBg.push(tripletToRgb(starTriplet));
        pointBorder.push('#fff');
      } else {
        pointBg.push(paletteRest[(i - 1) % paletteRest.length]);
        pointBorder.push('#fff');
      }
    }

    try {
      const radarChartInstance = new Chart(ctx, {
        type: 'radar',
        data: {
          labels: chartLabels,
          datasets: [{
            label: radarData.label || 'Product Performance',
            data: chartData,
            backgroundColor: tripletToRgba(starTriplet, 0.22),
            borderColor: tripletToRgb(starTriplet),
            pointBackgroundColor: pointBg,
            pointBorderColor: pointBorder,
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: pointBg,
            pointRadius: 4,
            pointBorderWidth: 2
          }]
        },
        options: {
          scales: {
            r: {
              beginAtZero: true,
              max: 10,
              ticks: {
                stepSize: 2,
                backdropColor: 'transparent'
              },
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              angleLines: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              pointLabels: {
                font: {
                  size: 11,
                  weight: '600'
                },
                color: '#374151'
              }
            }
          },
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              titleColor: '#1f2937',
              bodyColor: '#4b5563',
              borderColor: '#e5e7eb',
              borderWidth: 1,
              padding: 12,
              boxPadding: 6,
              usePointStyle: true,
              callbacks: {
                label: function(context) {
                  return context.label + ': ' + context.raw;
                }
              }
            }
          },
          elements: {
            line: {
              borderWidth: 2
            }
          },
          maintainAspectRatio: false
        }
      });
      
      radarCanvas.chartInstance = radarChartInstance;
      requestAnimationFrame(function() {
        try {
          radarChartInstance.resize();
        } catch (resizeErr) {
          /* noop */
        }
      });
    } catch (error) {
      console.error('Failed to initialize radar chart:', error);
    }
  }

  function applyRedStrikethrough(modal) {
    const priceContainer = modal.querySelector('.product-detail-pop-price');
    if (!priceContainer) return;
    const strikethroughElements = priceContainer.querySelectorAll('s, del, .price--compare, .price__compare, .price-item--compare, .price-item--regular, .price_was, [class*="compare"]:not([class*="current"]), [class*="regular"]:not([class*="current"]), [class*="was"]:not([class*="current"])');
    strikethroughElements.forEach(function(el) {
      if (!el.classList.contains('price_current') && !el.classList.contains('price-current') && !el.closest('.price_current') && !el.closest('.price-current')) {
        el.style.textDecoration = 'line-through';
        el.style.textDecorationColor = '#e53e3e';
        el.style.webkitTextDecorationColor = '#e53e3e';
        el.style.textDecorationThickness = '2px';
        el.style.webkitTextDecorationThickness = '2px';
      }
    });
    const currentPriceElements = priceContainer.querySelectorAll('.price_current, .price-current, [class*="current"]:not([class*="compare"]):not([class*="was"])');
    currentPriceElements.forEach(function(el) {
      el.style.textDecoration = 'none';
    });
  }

  /** 左栏雷达均分：由 radar JSON 的 data 数组算术平均，保留两位；无有效数据不展示 */
  function updateProductDetailPopRadarAverage(modal, radarData) {
    const root = modal.querySelector('.js-product-detail-pop-avg-root');
    if (!root || !radarData || !radarData.data || !radarData.data.length) return;
    const nums = [];
    for (let i = 0; i < radarData.data.length; i++) {
      const v = parseFloat(radarData.data[i], 10);
      if (!isNaN(v)) nums.push(v);
    }
    if (!nums.length) return;
    let sum = 0;
    for (let j = 0; j < nums.length; j++) sum += nums[j];
    const avg = (sum / nums.length).toFixed(2);
    const valEl = modal.querySelector('.js-product-detail-pop-avg');
    if (valEl) valEl.textContent = avg;
    root.removeAttribute('hidden');
  }

  function initProductDetailPop(modalId, radarData) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    const closeButtons = modal.querySelectorAll('.product-detail-pop-close, .product-detail-pop-close-btn');
    const overlay = modal;
    let keyDownHandler = null;
    
    function closeModal() {
      modal.style.display = 'none';
      modal.setAttribute('aria-hidden', 'true');
      
      if (keyDownHandler) {
        document.removeEventListener('keydown', keyDownHandler, true);
        keyDownHandler = null;
      }
      
      const otherOpenModals = document.querySelectorAll('.drawer[open], .modal:not([style*="display: none"]), .overlay.is-visible, [role="dialog"][aria-hidden="false"]:not(#' + modalId + ')');
      if (otherOpenModals.length === 0) {
        document.body.style.overflow = '';
      }
      
      const triggerButton = document.querySelector('[onclick*="openProductDetailPop' + modalId.replace('productDetailPop-', '') + '"]');
      if (triggerButton) {
        triggerButton.focus();
      }
    }

    // 优化图片加载：立即加载弹窗内的所有图片
    function preloadImages() {
      const images = modal.querySelectorAll('img[loading="lazy"], img[data-src], img[data-srcset]');
      images.forEach(function(img) {
        // 移除 lazy loading，立即加载
        if (img.hasAttribute('loading')) {
          img.removeAttribute('loading');
        }
        
        // 如果有 data-src，立即设置为 src
        if (img.hasAttribute('data-src')) {
          img.src = img.getAttribute('data-src');
          img.removeAttribute('data-src');
        }
        
        // 如果有 data-srcset，立即设置为 srcset
        if (img.hasAttribute('data-srcset')) {
          img.srcset = img.getAttribute('data-srcset');
          img.removeAttribute('data-srcset');
        }
        
        // 强制加载图片（即使已经在 DOM 中）
        if (img.src && !img.complete) {
          const tempImg = new Image();
          tempImg.src = img.src;
          if (img.srcset) {
            tempImg.srcset = img.srcset;
          }
        }
      });
    }
    
    function openModal() {
      // 显示加载状态
      modal.classList.add('is-loading');
      modal.style.display = 'flex';
      modal.setAttribute('aria-hidden', 'false');
      
      const otherOpenModals = document.querySelectorAll('.drawer[open], .modal:not([style*="display: none"]), .overlay.is-visible, [role="dialog"][aria-hidden="false"]:not(#' + modalId + ')');
      if (otherOpenModals.length === 0) {
        document.body.style.overflow = 'hidden';
      }
      
      // 使用 requestAnimationFrame 确保过渡效果流畅
      requestAnimationFrame(function() {
        // 预加载图片
        preloadImages();
        
        // 等待一帧后移除加载状态，显示内容
        requestAnimationFrame(function() {
          modal.classList.remove('is-loading');
          modal.classList.add('is-loaded');
          
          // 加载雷达图（如果需要）
          if (radarData && radarData.hasRadar) {
            const radarCanvas = document.getElementById(radarData.chartId);
            if (radarCanvas) {
              function initRadarAndAverage() {
                initRadarChart(radarCanvas, radarData);
                updateProductDetailPopRadarAverage(modal, radarData);
              }
              if (window.Chart) {
                initRadarAndAverage();
              } else {
                loadChartJS().then(function() {
                  initRadarAndAverage();
                }).catch(function(error) {
                  console.error('Failed to load Chart.js:', error);
                });
              }
            }
          }
        });
      });
      
      if (!keyDownHandler) {
        keyDownHandler = function(e) {
          if (e.key === 'Escape' && modal.style.display === 'flex') {
            const otherOpenModals = document.querySelectorAll('.drawer[open], .modal:not([style*="display: none"]), .overlay.is-visible, [role="dialog"][aria-hidden="false"]:not(#' + modalId + ')');
            if (otherOpenModals.length === 0) {
              e.stopPropagation();
              closeModal();
            }
          }
        };
        document.addEventListener('keydown', keyDownHandler, true);
      }
    }

    closeButtons.forEach(function(btn) {
      btn.addEventListener('click', closeModal);
    });

    overlay.addEventListener('click', function(e) {
      if (e.target === overlay) {
        closeModal();
      }
    });

    const openFunctionName = 'openProductDetailPop' + modalId.replace('productDetailPop-', '');
    // 标记这是真正的函数，不是占位函数
    openModal._isRealFunction = true;
    window[openFunctionName] = openModal;
  }
  
  // 将 initProductDetailPop 暴露到全局，供占位函数使用
  window.initProductDetailPop = initProductDetailPop;

  /**
   * 为 window.openProductDetailPop{Id} 注册占位：首次点击时 init，再调用真实 openModal。
   * defer 脚本在文档解析完成后执行，同步扫描即可覆盖首屏；动态插入由统一 MutationObserver 再扫描。
   */
  function ensureOpenProductDetailPopPlaceholder(productId) {
    if (!productId) return;
    const fnName = 'openProductDetailPop' + productId;
    if (window[fnName] && window[fnName]._isRealFunction) return;
    if (window[fnName]) return;

    window[fnName] = function() {
      if (window[fnName] && window[fnName]._isRealFunction) {
        return window[fnName].apply(this, arguments);
      }
      const modalId = 'productDetailPop-' + productId;

      function tryInitAndOpen(modal) {
        if (!modal) return;
        const radarDataScript = document.getElementById('productDetailPopRadarData-' + productId);
        let radarData = null;
        if (radarDataScript) {
          try {
            radarData = JSON.parse(radarDataScript.textContent);
          } catch (e) {
            console.error('Failed to parse radar data:', e);
          }
        }
        if (typeof window.initProductDetailPop === 'function') {
          window.initProductDetailPop(modalId, radarData);
        }
        if (window[fnName] && window[fnName]._isRealFunction) {
          return window[fnName].apply(this, arguments);
        }
        modal.style.display = 'flex';
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      }

      let modal = document.getElementById(modalId);
      if (modal) {
        tryInitAndOpen(modal);
        return;
      }
      let retryCount = 0;
      const maxRetries = 20;
      const retryInterval = setInterval(function() {
        retryCount++;
        modal = document.getElementById(modalId);
        if (modal) {
          clearInterval(retryInterval);
          tryInitAndOpen(modal);
        } else if (retryCount >= maxRetries) {
          clearInterval(retryInterval);
          console.warn('Product detail pop not available for product:', productId);
        }
      }, 100);
    };
  }

  function scanAndCreatePlaceholders() {
    try {
      document.querySelectorAll('[onclick*="openProductDetailPop"]').forEach(function(button) {
        const onclickAttr = button.getAttribute('onclick');
        if (!onclickAttr || onclickAttr.indexOf('openProductDetailPop') === -1) return;
        const match = onclickAttr.match(/openProductDetailPop(\d+)/);
        if (match && match[1]) ensureOpenProductDetailPopPlaceholder(match[1]);
      });
      document.querySelectorAll('[id^="productDetailPop-"]').forEach(function(modal) {
        const modalId = modal.id;
        if (!modalId || modalId.indexOf('productDetailPop-') !== 0) return;
        const productId = modalId.replace('productDetailPop-', '');
        if (productId) ensureOpenProductDetailPopPlaceholder(productId);
      });
    } catch (e) {
      console.error('Error in scanAndCreatePlaceholders:', e);
    }
  }

  scanAndCreatePlaceholders();

  // ============================================
  // 5. X TECH DRAWER
  // ============================================
  addInit(function initXTechDrawer() {
    if (document.body.dataset.xTechDrawerBound === 'true') return;
    document.body.dataset.xTechDrawerBound = 'true';
    const drawer = document.getElementById('x-tech-drawer-global');
    if (!drawer) return;
    const titleNode = drawer.querySelector('[data-x-tech-drawer-title]');
    const subtitleNode = drawer.querySelector('[data-x-tech-drawer-subtitle]');
    const noteNode = drawer.querySelector('[data-x-tech-drawer-note]');
    const bodyNode = drawer.querySelector('[data-x-tech-drawer-body]');
    const footerCloseBtn = drawer.querySelector('[data-x-tech-drawer-footer-close]');
    const headerCloseBtn = drawer.querySelector('.x-tech-drawer__close');

    function closeDeliveryTimeOverlaysForDrawer() {
      try {
        const triggers = document.querySelectorAll('.delivery-time__tooltip-trigger.active');
        triggers.forEach(function(trigger) {
          trigger.classList.remove('active');
        });

        const tooltips = document.querySelectorAll('.delivery-time__tooltip.active, body > .delivery-time__tooltip.active, .delivery-time__tooltip');
        tooltips.forEach(function(tooltip) {
          tooltip.classList.remove('active');
          tooltip.style.display = 'none';
          tooltip.style.opacity = '';
          tooltip.style.visibility = '';
          tooltip.style.pointerEvents = '';
        });

        const overlay = document.querySelector('.delivery-time-tooltip-overlay');
        if (overlay) {
          overlay.style.display = 'none';
          overlay.style.opacity = '';
          overlay.style.visibility = '';
          overlay.style.pointerEvents = '';
        }
      } catch (e) {
        // keep drawer open flow safe even if tooltip cleanup fails
      }
    }

    function setDrawerState(isOpen, trigger) {
      if (!drawer) return;
      drawer.classList.toggle('is-open', isOpen);
      drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');

      if (trigger) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        if (isOpen) {
          drawer.dataset.xTriggerId = trigger.id || '';
        }
      }

      if (isOpen) {
        closeDeliveryTimeOverlaysForDrawer();
        document.body.style.overflow = 'hidden';
        document.body.classList.add('is-x-tech-drawer-open');
        const panel = drawer.querySelector('.x-tech-drawer__panel');
        if (panel) panel.focus({ preventScroll: true });
      } else {
        const openDrawers = document.querySelectorAll('.x-tech-drawer.is-open');
        if (openDrawers.length === 0) {
          document.body.classList.remove('is-x-tech-drawer-open');
          xMayReleaseBodyScroll();
        }
        const triggerId = drawer.dataset.xTriggerId;
        if (triggerId) {
          const prevTrigger = document.getElementById(triggerId);
          if (prevTrigger) prevTrigger.focus({ preventScroll: true });
        }
      }
    }

    function fillDrawerFromTrigger(trigger) {
      if (!trigger || !bodyNode) return false;
      const templateId = trigger.getAttribute('data-x-tech-drawer-template');
      const template = templateId ? document.getElementById(templateId) : null;
      if (!template) return false;

      bodyNode.innerHTML = template.innerHTML;

      const title = trigger.getAttribute('data-x-tech-drawer-title') || 'Technical Specifications';
      const subtitle = trigger.getAttribute('data-x-tech-drawer-subtitle') || '';
      const closeText = trigger.getAttribute('data-x-tech-drawer-close-text') || 'Close';
      const footerNote = trigger.getAttribute('data-x-tech-drawer-footer-note') || '';

      if (titleNode) titleNode.textContent = title;
      if (subtitleNode) {
        subtitleNode.textContent = subtitle;
        subtitleNode.hidden = subtitle === '';
      }
      if (noteNode) {
        noteNode.textContent = footerNote;
        noteNode.hidden = footerNote === '';
      }
      if (footerCloseBtn) footerCloseBtn.textContent = closeText;
      if (headerCloseBtn) headerCloseBtn.setAttribute('aria-label', closeText);
      return true;
    }

    document.addEventListener('click', function onTechDrawerClick(e) {
      const trigger = e.target.closest('[data-x-tech-drawer-trigger]');
      if (trigger) {
        e.preventDefault();
        const opened = fillDrawerFromTrigger(trigger);
        if (!opened) return;
        setDrawerState(true, trigger);
        return;
      }

      const closeBtn = e.target.closest('[data-x-tech-drawer-close]');
      if (closeBtn) {
        setDrawerState(false);
      }
    });

    document.addEventListener('keydown', function onTechDrawerKeydown(e) {
      if (e.key !== 'Escape') return;
      if (!drawer.classList.contains('is-open')) return;
      e.stopPropagation();
      setDrawerState(false);
    }, true);
  });

  function xMayReleaseBodyScroll() {
    const openDrawers = document.querySelectorAll('.x-tech-drawer.is-open').length;
    const openTrust = document.querySelectorAll('.x-trust-modal.is-open').length;
    const openVl = document.querySelectorAll('.x-vl-modal.is-open').length;
    if (!openDrawers && !openTrust && !openVl) {
      document.body.style.overflow = '';
    }
  }

  // ============================================
  // 6. X TRUST BADGES MODAL
  // ============================================
  addInit(function initXTrustBadgesModal() {
    if (document.body.dataset.xTrustModalBound === 'true') return;
    document.body.dataset.xTrustModalBound = 'true';

    function closeTrustModal(modal) {
      if (!modal) return;
      modal.setAttribute('aria-hidden', 'true');
      modal.hidden = true;
      modal.classList.remove('is-open');

      xMayReleaseBodyScroll();

      const triggerId = modal.dataset.xTrustTriggerId;
      if (triggerId) {
        const trigger = document.getElementById(triggerId);
        if (trigger) trigger.focus({ preventScroll: true });
      }
    }

    function openTrustModal(trigger) {
      if (!trigger) return;
      const block = trigger.closest('.x-trust-badges');
      if (!block) return;
      const modal = block.querySelector('.x-trust-modal');
      if (!modal) return;

      const contentId = trigger.getAttribute('data-x-trust-content-id');
      const contentSource = contentId ? document.getElementById(contentId) : null;
      if (!contentSource) return;

      document.querySelectorAll('.x-vl-modal.is-open').forEach(function(vlModal) {
        vlModal.classList.remove('is-open');
        vlModal.setAttribute('aria-hidden', 'true');
        vlModal.hidden = true;
        const media = vlModal.querySelector('[data-x-vl-modal-media]');
        if (media) media.innerHTML = '';
      });

      document.querySelectorAll('.x-trust-modal.is-open').forEach(function(opened) {
        closeTrustModal(opened);
      });

      let triggerId = trigger.id;
      if (!triggerId) {
        triggerId = 'x-trust-trigger-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        trigger.id = triggerId;
      }
      modal.dataset.xTrustTriggerId = triggerId;

      const title = trigger.getAttribute('data-x-trust-title') || '';
      const titleNode = modal.querySelector('.x-trust-modal__title');
      const contentNode = modal.querySelector('[data-x-trust-modal-content]');
      const iconNode = modal.querySelector('[data-x-trust-modal-icon]');
      const triggerIcon = trigger.querySelector('.x-trust-badges__icon');
      if (titleNode) titleNode.textContent = title;
      if (contentNode) contentNode.innerHTML = contentSource.innerHTML;
      if (iconNode) {
        iconNode.innerHTML = '';
        if (triggerIcon) {
          iconNode.appendChild(triggerIcon.cloneNode(true));
        }
      }

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    document.addEventListener('click', function onTrustModalClick(e) {
      const openTrigger = e.target.closest('[data-x-trust-open]');
      if (openTrigger) {
        e.preventDefault();
        openTrustModal(openTrigger);
        return;
      }

      const closeTrigger = e.target.closest('[data-x-trust-close]');
      if (closeTrigger) {
        const modal = closeTrigger.closest('.x-trust-modal');
        if (modal) closeTrustModal(modal);
      }
    });

    document.addEventListener('keydown', function onTrustModalEsc(e) {
      if (e.key !== 'Escape') return;
      const openedModal = document.querySelector('.x-trust-modal.is-open');
      if (!openedModal) return;
      e.stopPropagation();
      closeTrustModal(openedModal);
    }, true);
  });

  // ============================================
  // 7. X VIDEO LIST MODAL
  // ============================================
  addInit(function initXVideoListModal() {
    if (document.body.dataset.xVlModalBound === 'true') return;
    document.body.dataset.xVlModalBound = 'true';

    function closeVlModal(modal) {
      if (!modal) return;
      const media = modal.querySelector('[data-x-vl-modal-media]');
      if (media) {
        media.querySelectorAll('video').forEach(function(v) {
          try {
            v.pause();
          } catch (err) {}
        });
        media.querySelectorAll('iframe').forEach(function(f) {
          f.removeAttribute('src');
          f.setAttribute('src', 'about:blank');
        });
        media.innerHTML = '';
      }
      modal.setAttribute('aria-hidden', 'true');
      modal.hidden = true;
      modal.classList.remove('is-open');
      xMayReleaseBodyScroll();
      const triggerId = modal.dataset.xVlTriggerId;
      if (triggerId) {
        const trigger = document.getElementById(triggerId);
        if (trigger) trigger.focus({ preventScroll: true });
      }
    }

    function openVlModal(trigger) {
      if (!trigger) return;
      const sectionEl = trigger.closest('[data-x-video-list]');
      if (!sectionEl) return;
      const sid = sectionEl.getAttribute('data-section-id');
      if (!sid) return;
      const modal = document.getElementById('x-vl-modal-' + sid);
      if (!modal) return;

      const tplId = trigger.getAttribute('data-x-vl-template-id');
      const tpl = tplId ? document.getElementById(tplId) : null;
      if (!tpl || tpl.tagName !== 'TEMPLATE') return;

      document.querySelectorAll('.x-vl-modal.is-open').forEach(function(m) {
        closeVlModal(m);
      });

      document.querySelectorAll('.x-trust-modal.is-open').forEach(function(m) {
        m.classList.remove('is-open');
        m.setAttribute('aria-hidden', 'true');
        m.hidden = true;
      });

      let triggerId = trigger.id;
      if (!triggerId) {
        triggerId = 'x-vl-trigger-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
        trigger.id = triggerId;
      }
      modal.dataset.xVlTriggerId = triggerId;

      const title = trigger.getAttribute('data-x-vl-title') || '';
      const titleNode = modal.querySelector('.x-vl-modal__title');
      const mediaNode = modal.querySelector('[data-x-vl-modal-media]');

      if (titleNode) titleNode.textContent = title;

      let dm = null;
      if (mediaNode) {
        mediaNode.innerHTML = '';
        mediaNode.appendChild(tpl.content.cloneNode(true));
        dm = mediaNode.querySelector('deferred-media');
      }

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          if (dm && typeof dm.loadContent === 'function') {
            dm.loadContent(true, true, 'click');
          }
          const closeBtn = modal.querySelector('.x-vl-modal__close');
          if (closeBtn) closeBtn.focus({ preventScroll: true });
        });
      });
    }

    document.addEventListener('pointerdown', function onVlPointerDown(e) {
      const el = e.target.closest('[data-x-vl-open]');
      if (!el) return;
      el.dataset.vlPtrX = String(e.clientX);
      el.dataset.vlPtrY = String(e.clientY);
    }, true);

    document.addEventListener('click', function onVlModalClick(e) {
      const openEl = e.target.closest('[data-x-vl-open]');
      if (openEl) {
        const x0 = parseFloat(openEl.dataset.vlPtrX || 'NaN');
        const y0 = parseFloat(openEl.dataset.vlPtrY || 'NaN');
        if (!Number.isNaN(x0) && !Number.isNaN(y0)) {
          if (Math.abs(e.clientX - x0) > 12 || Math.abs(e.clientY - y0) > 12) {
            return;
          }
        }
        e.preventDefault();
        openVlModal(openEl);
        return;
      }

      const closeTr = e.target.closest('[data-x-vl-close]');
      if (closeTr) {
        const modal = closeTr.closest('.x-vl-modal');
        if (modal) closeVlModal(modal);
      }
    }, true);

    document.addEventListener('keydown', function onVlModalEsc(e) {
      if (e.key !== 'Escape') return;
      const opened = document.querySelector('.x-vl-modal.is-open');
      if (!opened) return;
      e.stopPropagation();
      closeVlModal(opened);
    }, true);
  });

  // ============================================
  // UNIFIED MUTATION OBSERVER
  // ============================================
  if (typeof MutationObserver !== 'undefined') {
    let debounceTimer = null;
    let runDeliveryNext = false;
    let runPopScanNext = false;

    const observer = new MutationObserver(function(mutations) {
      const maxMutations = mutations.length > 50 ? 50 : mutations.length;
      let sawDelivery = false;
      let sawPop = false;

      for (let i = 0; i < maxMutations; i++) {
        const addedNodes = mutations[i].addedNodes;
        for (let j = 0; j < addedNodes.length; j++) {
          const node = addedNodes[j];
          if (node.nodeType !== 1) continue;

          if (node.classList && (
            node.classList.contains('drawer') ||
            node.classList.contains('cart-drawer') ||
            node.classList.contains('quick-add-drawer') ||
            (node.id && typeof node.id === 'string' && node.id.indexOf('cart') !== -1)
          )) {
            continue;
          }

          if (!sawDelivery) {
            if (node.classList && node.classList.contains('delivery-time__tooltip-trigger')) {
              sawDelivery = true;
            } else if (node.querySelector && node.querySelector('.delivery-time__tooltip-trigger')) {
              sawDelivery = true;
            }
          }

          if (!sawPop) {
            if (node.id && typeof node.id === 'string' && node.id.indexOf('productDetailPop-') === 0) {
              sawPop = true;
            } else if (node.hasAttribute && node.getAttribute('onclick') && node.getAttribute('onclick').indexOf('openProductDetailPop') !== -1) {
              sawPop = true;
            } else if (node.querySelector && (node.querySelector('[id^="productDetailPop-"]') || node.querySelector('[onclick*="openProductDetailPop"]'))) {
              sawPop = true;
            }
          }

          if (sawDelivery && sawPop) break;
        }
        if (sawDelivery && sawPop) break;
      }

      if (sawDelivery) runDeliveryNext = true;
      if (sawPop) runPopScanNext = true;
      if (!sawDelivery && !sawPop) return;

      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        debounceTimer = null;
        const doDelivery = runDeliveryNext;
        const doPop = runPopScanNext;
        runDeliveryNext = false;
        runPopScanNext = false;
        if (doDelivery) {
          initDeliveryTimeDates();
          setTimeout(initDeliveryTimeTooltip, 50);
        }
        if (doPop) {
          scanAndCreatePlaceholders();
        }
      }, 100);
    });

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

})();